"use client";

// Italian copy of components/widgetsLibraryV2 for /it/widgets.
// Lists every widget, like the English page. Cards for widgets with an Italian
// page use its Italian name and link there; the rest keep their English name,
// link to the English page and carry an EN tag. Every description, category
// and label is Italian (i18n/it/library.ts). The platform-page variant of the
// English component is left out: the Italian site has no platform pages yet.

import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FiSearch } from "@react-icons/all-files/fi/FiSearch";
import { FiX } from "@react-icons/all-files/fi/FiX";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { FiGrid } from "@react-icons/all-files/fi/FiGrid";
import { FiStar } from "@react-icons/all-files/fi/FiStar";
import { FiLayout } from "@react-icons/all-files/fi/FiLayout";
import { FiClipboard } from "@react-icons/all-files/fi/FiClipboard";
import { FiRss } from "@react-icons/all-files/fi/FiRss";
import { FiMessageCircle } from "@react-icons/all-files/fi/FiMessageCircle";
import { FiImage } from "@react-icons/all-files/fi/FiImage";
import { FiClock } from "@react-icons/all-files/fi/FiClock";
import { FiAward } from "@react-icons/all-files/fi/FiAward";

import type { ILibraryCategory } from "../../helpers/widgetsLibraryData";
import type { IEsLibraryApp } from "@/i18n/it/library";
import { itHref } from "@/i18n/it/routes";
import { searchWidgetsIt } from "@/i18n/it/search";
import { SocialProofIt } from "./socialProofIt.comp";
import { FaqIt } from "./faqIt.comp";
import { EnTag } from "./langSwitch.comp";
import { DEFAULT_PLATFORM_TILES } from "../widgetPlatformCarousel/widgetPlatformCarousel.data";
import { StyledWidgetsLibraryV2 } from "../widgetsLibraryV2/widgetsLibraryV2.styles";

interface IWidgetsLibraryEsProps {
  apps: IEsLibraryApp[];
  // Category names are already Italian (i18n/it/library.ts).
  categories: ILibraryCategory[];
}

const LIST_PATH = itHref("/widgets");

const PAGE_SIZE = 24;
const SUGGESTION_LIMIT = 8;
const NEW_DAYS = 90;
const FEATURED_TAGS = ["Popular", "Trending"];
// Hand-picked by Sergei (2026-09-29), shown in this order.
const TOP_WIDGET_SLUGS = [
  "google-reviews",
  "audio-player",
  "brackets",
  "calendar",
  "age-verification",
  "logo-showcase",
  "before-after",
  "image-hotspot",
  "timeline",
  "countdown",
  "instagram-feed",
  "ai-chatbot",
];
// Where the faint platform marks sit in the hero background: left and right
// edges only, so nothing sits behind the headline or the search box.
const HERO_MARKS = [
  { x: 4, y: 14, size: 34, r: -8 },
  { x: 13, y: 34, size: 26, r: 6 },
  { x: 6, y: 56, size: 40, r: 4 },
  { x: 15, y: 74, size: 28, r: -6 },
  { x: 3, y: 88, size: 24, r: 10 },
  { x: 20, y: 12, size: 22, r: 4 },
  { x: 22, y: 90, size: 30, r: -4 },
  { x: 93, y: 12, size: 30, r: 8 },
  { x: 84, y: 30, size: 24, r: -6 },
  { x: 92, y: 50, size: 38, r: -4 },
  { x: 83, y: 70, size: 30, r: 6 },
  { x: 94, y: 86, size: 26, r: -10 },
  { x: 76, y: 10, size: 22, r: -4 },
  { x: 75, y: 90, size: 28, r: 6 },
];
// One line icon per category chip (design B, 2026-09-29).
const CATEGORY_ICONS: Record<string, React.ComponentType> = {
  "Recensioni e fiducia": FiStar,
  "Popup e barre": FiLayout,
  "Moduli e prenotazioni": FiClipboard,
  "Feed social": FiRss,
  "Chat e contatti": FiMessageCircle,
  "Immagini e video": FiImage,
  "Timer e contatori": FiClock,
  "Giochi e quiz": FiAward,
};

// ?category= links that use the English names (the English page's groups
// and its older DB category names) open the matching Italian group.
const LEGACY_CATEGORY_LINKS: Record<string, string> = {
  "Reviews & trust": "Recensioni e fiducia",
  "Popups & bars": "Popup e barre",
  "Forms & bookings": "Moduli e prenotazioni",
  "Social feeds": "Feed social",
  "Chat & contact": "Chat e contatti",
  "Images & video": "Immagini e video",
  "Timers & counters": "Timer e contatori",
  "Games & quizzes": "Giochi e quiz",
  Reviews: "Recensioni e fiducia",
  "Popups, Bars & Buttons": "Popup e barre",
  Forms: "Moduli e prenotazioni",
  "Social Feeds": "Feed social",
  Chats: "Chat e contatti",
  "Images & Galleries": "Immagini e video",
  Media: "Immagini e video",
  Gaming: "Giochi e quiz",
};

const QUICK_SEARCHES = [
  "Recensioni Google",
  "Feed Instagram",
  "Modulo di contatto",
  "Countdown",
];

// Badge text; the class name keeps the English word, which the styles target.
const BADGE_IT: Record<string, string> = {
  New: "Nuovo",
  Popular: "Popolare",
  Trending: "Di tendenza",
};

const FAQS = [
  {
    q: "Cos'è un widget per siti web?",
    a: "Un widget per siti web è una piccola funzione autonoma che aggiungi a una pagina, come un muro di recensioni, un feed Instagram, un modulo di contatto o un conto alla rovescia. Lo progetti nell'editor di Common Ninja e incolli una riga di codice di incorporamento nel punto in cui vuoi che appaia.",
  },
  {
    q: "I widget di Common Ninja sono gratuiti?",
    a: "Sì. Per tutti i nostri widget è disponibile un piano gratuito, senza bisogno di carta di credito. I piani a pagamento aggiungono funzioni premium e rimuovono i limiti quando ti servono.",
  },
  {
    q: "Su quali piattaforme funzionano i widget?",
    a: "Funzionano su oltre 200 piattaforme, tra cui WordPress, Shopify, Wix, Squarespace, Webflow e i siti in semplice HTML. Se il tuo sito ti permette di incollare un codice di incorporamento, puoi aggiungere un widget Common Ninja.",
  },
  {
    q: "Devo saper programmare?",
    a: "No. Ogni widget si personalizza visivamente nell'editor: layout, colori, font e contenuti. Quando il risultato ti convince, copi il codice di incorporamento e lo incolli nel tuo sito. Le modifiche che fai in seguito si aggiornano automaticamente.",
  },
  {
    q: "Non trovi il widget che ti serve?",
    a: 'Prova <a href="https://embeddable.co?ref=commonninja" target="_blank" hreflang="en">Embeddable</a> (in inglese), il nostro creatore di widget con IA. Descrivi ciò che ti serve e crea per te un widget su misura.',
  },
];

function isNew(app: IEsLibraryApp) {
  if (!app.created) return false;
  return Date.now() - new Date(app.created).getTime() < NEW_DAYS * 86400000;
}

function badgeFor(app: IEsLibraryApp) {
  if (isNew(app)) return "New";
  return app.tags.find((t) => FEATURED_TAGS.includes(t));
}

const categoryHref = (name: string) =>
  `${LIST_PATH}?category=${encodeURIComponent(name)}`;

const linkFor = (app: IEsLibraryApp) => app.href;

function WidgetCard({ app, eager }: { app: IEsLibraryApp; eager?: boolean }) {
  const badge = badgeFor(app);
  return (
    <Link
      href={linkFor(app)}
      className={`card ${badge === "New" ? "card-new" : ""}`}
      title={`Widget ${app.name}`}
      hrefLang={app.isEn ? "en" : "it"}
    >
      <span className="card-preview">
        <Image
          src={app.preview || app.logo}
          alt={`Anteprima del widget ${app.name}`}
          width={320}
          height={170}
          priority={eager}
          loading={eager ? undefined : "lazy"}
        />
        {badge && (
          <span className={`badge badge-${badge.toLowerCase()}`}>
            {BADGE_IT[badge] || badge}
          </span>
        )}
      </span>
      <span className="card-body">
        <span className="card-title">
          <Image src={app.logo} alt="" width={28} height={28} />
          <span>
            <strong>
              {app.name}
              {app.isEn && <EnTag />}
            </strong>
            {app.categories[0] && <small>{app.categories[0]}</small>}
          </span>
        </span>
        <span className="card-desc">{app.teaser}</span>
      </span>
    </Link>
  );
}

export const WidgetsLibraryIt = ({
  apps,
  categories,
}: IWidgetsLibraryEsProps) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const gridRef = useRef<HTMLElement>(null);
  const chipsSentinel = useRef<HTMLDivElement>(null);
  const [chipsStuck, setChipsStuck] = useState(false);
  const [chipsTop, setChipsTop] = useState(0);

  // The chip bar is sticky, and so is the site header: it hides while you
  // scroll down and floats back in (position: fixed) when you scroll up. So
  // the bar sits just under the header whenever the header is showing, and
  // at the very top otherwise. "Stuck" (the stronger floating look) means the
  // sentinel above the bar has scrolled past where the bar pins.
  useEffect(() => {
    let raf = 0;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const measure = () => {
      raf = 0;
      let offset = 0;
      document.querySelectorAll("header").forEach((h) => {
        const cs = window.getComputedStyle(h);
        if (cs.position !== "fixed" || cs.display === "none") return;
        const bottom = h.getBoundingClientRect().bottom;
        if (bottom > 0) offset = Math.max(offset, Math.round(bottom) + 8);
      });
      setChipsTop(offset);
      const sentinel = chipsSentinel.current;
      setChipsStuck(
        !!sentinel && sentinel.getBoundingClientRect().top < offset
      );
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
      // The header slides in over 0.3s; measure again once it has landed.
      clearTimeout(settle);
      settle = setTimeout(measure, 350);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, []);
  const urlReady = useRef(false);

  // Read ?search= / ?category= after mount, so the static HTML always holds
  // the full, unfiltered list of widget links for crawlers.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("search") || "";
    const c = params.get("category") || "";
    // Old ?category= links use the DB category names; send them to the group
    // that replaced them. Anything unknown falls back to "All".
    const name = LEGACY_CATEGORY_LINKS[c] || c;
    const match = categories.find(
      (cat) => cat.name.toLowerCase() === name.toLowerCase()
    );
    if (s) setQuery(s);
    if (match) setCategory(match.name);
  }, [categories]);

  useEffect(() => {
    // Skip the first run, so the effect above reads the URL before it is rewritten.
    if (!urlReady.current) {
      urlReady.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (query.trim()) params.set("search", query.trim());
    else params.delete("search");
    if (category) params.set("category", category);
    else params.delete("category");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
    setVisible(PAGE_SIZE);
  }, [query, category]);

  const popular = useMemo(
    () =>
      TOP_WIDGET_SLUGS.map((slug) => apps.find((a) => a.enHref === `/widgets/${slug}`)).filter(
        (a): a is IEsLibraryApp => Boolean(a)
      ),
    [apps]
  );

  const newest = useMemo(
    () =>
      [...apps]
        .filter(isNew)
        .sort((a, b) => b.created.localeCompare(a.created))
        .slice(0, 4),
    [apps]
  );

  const matches = useMemo(
    () => (query.trim().length > 1 ? searchWidgetsIt(apps, query) : []),
    [apps, query]
  );
  const suggestions = matches.slice(0, SUGGESTION_LIMIT);

  // Keep the highlighted suggestion visible when arrowing through the list.
  useEffect(() => {
    if (activeSuggestion < 0) return;
    document
      .getElementById(`widget-suggestion-${activeSuggestion}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeSuggestion]);

  const results = useMemo(() => {
    if (!query.trim() && !category) {
      // The rows above already show these, so they go last instead of
      // repeating at the top. Every widget still appears exactly once.
      const shownAbove = new Set([...popular, ...newest].map((a) => a._id));
      return [
        ...apps.filter((a) => !shownAbove.has(a._id)),
        ...apps.filter((a) => shownAbove.has(a._id)),
      ];
    }
    const inCategory = category
      ? apps.filter((a) => a.categories.includes(category))
      : apps;
    return query.trim() ? searchWidgetsIt(inCategory, query) : inCategory;
  }, [apps, query, category, popular, newest]);

  const isFiltered = Boolean(query.trim() || category);
  const shown = Math.min(visible, results.length);

  // Picking a category or searching hides the Top widgets and Recently added
  // rows, which moves the grid up the page. Scrolling in the click handler
  // measured the old layout and overshot by the height of those rows, landing
  // at the bottom on short categories. So only ask for the scroll here, and do
  // it after React has drawn the new layout.
  const [scrollRequest, setScrollRequest] = useState(0);
  useEffect(() => {
    if (!scrollRequest) return;
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollRequest]);

  function scrollToGrid() {
    setScrollRequest((n) => n + 1);
  }

  function pickCategory(e: React.MouseEvent, name: string) {
    e.preventDefault();
    setCategory(name);
    setQuery("");
    scrollToGrid();
  }

  function runSearch(term: string) {
    setQuery(term);
    setCategory("");
    setSuggestOpen(false);
    scrollToGrid();
  }

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestOpen || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeSuggestion >= 0) {
      e.preventDefault();
      window.location.href = linkFor(suggestions[activeSuggestion]);
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  }

  const gridTitle = category
    ? `Widget: ${category}`
    : query.trim()
    ? `Risultati per “${query.trim()}”`
    : "Tutti i widget";

  return (
    <StyledWidgetsLibraryV2>
      <section className="hero">
        {/* Faint platform marks around the edges: the "any platform" line,
            said quietly by the background. Decorative only. */}
        <div className="hero-marks" aria-hidden="true">
          {HERO_MARKS.map((m, i) => {
            const p = DEFAULT_PLATFORM_TILES[i % DEFAULT_PLATFORM_TILES.length];
            return (
              <svg
                key={p.slug}
                viewBox="0 0 24 24"
                style={{
                  left: `${m.x}%`,
                  top: `${m.y}%`,
                  width: m.size,
                  height: m.size,
                  transform: `rotate(${m.r}deg)`,
                }}
              >
                <path d={p.iconPath} />
              </svg>
            );
          })}
        </div>
        <div className="hero-inner">
          <div className="hero-proof">
            <SocialProofIt avatarSize="small" />
          </div>
          <h1>
            <span className="gradient">Widget gratuiti</span> per il tuo sito.{" "}
            <span className="second-line">Ogni piattaforma, zero codice.</span>
          </h1>
          <p className="lead">
            Metti in mostra le recensioni, raccogli contatti, ricevi prenotazioni
            e vendi di più con widget pronti all&apos;uso. Adatta ogni widget al
            tuo brand nell&apos;editor visivo, poi incolla una riga di codice su
            WordPress, Shopify, Wix o qualsiasi altro sito.
          </p>

          <form
            className="search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(query);
            }}
          >
            <FiSearch className="search-icon" aria-hidden />
            <input
              type="search"
              role="combobox"
              aria-label="Cerca widget"
              aria-autocomplete="list"
              aria-expanded={suggestOpen && suggestions.length > 0}
              aria-controls="widget-suggestions"
              placeholder="Cerca un widget, es. recensioni, modulo, countdown"
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setCategory("");
                setSuggestOpen(true);
                setActiveSuggestion(-1);
              }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              onKeyDown={onSearchKey}
            />
            <button type="submit">Cerca</button>
            {suggestOpen && suggestions.length > 0 && (
              <div className="suggest-panel">
              <ul className="suggestions" id="widget-suggestions" role="listbox">
                {suggestions.map((app, i) => (
                  <li
                    key={app._id}
                    id={`widget-suggestion-${i}`}
                    role="option"
                    aria-selected={i === activeSuggestion}
                  >
                    <Link href={linkFor(app)}>
                      <Image src={app.logo} alt="" width={28} height={28} />
                      <span>
                        <strong>
                          {app.name}
                          {app.isEn && <EnTag />}
                        </strong>
                        <small>{app.categories[0]}</small>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {matches.length > suggestions.length && (
                <button
                  type="button"
                  className="suggest-all"
                  // mousedown fires before the input's blur closes the panel
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => runSearch(query)}
                >
                  Vedi tutti i {matches.length} risultati <FiArrowRight />
                </button>
              )}
              </div>
            )}
          </form>

          <div className="quick">
            <span>Ricerche popolari:</span>
            {QUICK_SEARCHES.map((term) => (
              <button key={term} type="button" onClick={() => runSearch(term)}>
                {term}
              </button>
            ))}
          </div>

          <div className="scores">
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4,9</strong> su Google
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>
              <span className="star" aria-hidden="true">★</span> <strong>4,3</strong> su Trustpilot
            </span>
            <span className="dot" aria-hidden="true">·</span>
            <span>Piano gratuito per ogni widget</span>
          </div>

        </div>
      </section>


      <div ref={chipsSentinel} aria-hidden="true" />
      <nav
        className={`chips ${chipsStuck ? "is-stuck" : ""}`}
        aria-label="Categorie di widget"
        style={{ top: chipsTop }}
      >
        <div className="chips-track">
          <a
            href={LIST_PATH}
            className={`chip ${!category ? "active" : ""}`}
            onClick={(e) => pickCategory(e, "")}
          >
            <FiGrid aria-hidden="true" /> Tutti
          </a>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.name] || FiGrid;
            return (
              <a
                key={cat.name}
                href={categoryHref(cat.name)}
                className={`chip ${category === cat.name ? "active" : ""}`}
                onClick={(e) => pickCategory(e, cat.name)}
              >
                <Icon aria-hidden="true" /> {cat.name}
              </a>
            );
          })}
        </div>
      </nav>

      <main className="main">
        {!isFiltered && (
          <>
            <section className="row">
              <div className="row-head">
                <h2>Widget in evidenza</h2>
                <p>I widget da cui parte la maggior parte dei siti.</p>
              </div>
              <div className="grid grid-scroll">
                {popular.map((app, i) => (
                  <WidgetCard key={app._id} app={app} eager={i < 4} />
                ))}
              </div>
            </section>

            {newest.length > 0 && (
              <section className="row">
                <div className="row-head">
                  <h2>Aggiunti di recente</h2>
                  <p>I nuovi widget degli ultimi mesi.</p>
                </div>
                <div className="grid grid-scroll">
                  {newest.map((app) => (
                    <WidgetCard key={app._id} app={app} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <section className="row" ref={gridRef} id="all-widgets">
          <div className="row-head row-head-split">
            <div>
              <h2>{gridTitle}</h2>
              <p aria-live="polite">
                {results.length
                  ? `Mostrati ${shown} di ${results.length} widget`
                  : "Nessun widget corrispondente"}
              </p>
            </div>
            {isFiltered && (
              <button
                type="button"
                className="clear"
                onClick={() => {
                  setQuery("");
                  setCategory("");
                }}
              >
                Azzera i filtri <FiX />
              </button>
            )}
          </div>

          {results.length > 0 ? (
            <div className="grid grid-compact">
              {results.map((app, i) => (
                <div key={app._id} className={i >= visible ? "is-hidden" : undefined}>
                  <WidgetCard app={app} />
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              <h3>Nessun widget corrisponde ancora a “{query.trim()}”</h3>
              <p>Prova una di queste categorie, o parti da un widget popolare.</p>
              <div className="empty-chips">
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.name}
                    href={categoryHref(cat.name)}
                    className="chip"
                    onClick={(e) => pickCategory(e, cat.name)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="grid">
                {popular.slice(0, 4).map((app) => (
                  <WidgetCard key={app._id} app={app} />
                ))}
              </div>
              <p className="empty-embd">
                Ti serve qualcosa su misura?{" "}
                <Link href="https://embeddable.co?ref=commonninja" target="_blank" hrefLang="en">
                  Crealo con Embeddable, il nostro creatore di widget con IA
                  <EnTag />
                </Link>
              </p>
            </div>
          )}

          {results.length > visible && (
            <div className="more">
              <button type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Mostra altri {Math.min(PAGE_SIZE, results.length - visible)} widget
              </button>
            </div>
          )}
        </section>


        <section className="steps">
          <h2>
            Scegli, personalizza, <span className="gradient">incorpora</span>
          </h2>
          <ol>
            <li>
              <span className="step-num">1</span>
              <strong>Scegli un widget</strong>
              <p>Sfoglia la libreria e parti da un modello pronto all&apos;uso.</p>
            </li>
            <li>
              <span className="step-num">2</span>
              <strong>Rendilo tuo</strong>
              <p>Modifica layout, colori, font e contenuti nell&apos;editor visivo. Senza codice.</p>
            </li>
            <li>
              <span className="step-num">3</span>
              <strong>Incorporalo ovunque</strong>
              <p>
                Incolla una riga di codice su una qualsiasi delle oltre 200
                piattaforme. Le modifiche successive si aggiornano da sole.
              </p>
            </li>
          </ol>
          <Link href="/signup" className="steps-cta">
            Inizia gratis <FiArrowRight />
          </Link>
          <p className="steps-note">
            Gratis per sempre. Nessuna carta di credito richiesta. L&apos;editor è
            in inglese per ora.
          </p>
        </section>

        <section className="faq-wrap">
          <FaqIt faqs={FAQS} />
        </section>
      </main>
    </StyledWidgetsLibraryV2>
  );
};
